/**
 * SceneEnhancements — Phase 8 Library Edition (continuation pass).
 *
 * Tom's third-pass live-site feedback: "the scene reads as flat horizontal
 * bands (sky / terrain / ground). Make it more interesting." This component
 * adds three cheap, AESTHETIC-BRIEF-respecting visual layers that give the
 * scene more character without crossing into PBR / shader / photoreal
 * territory:
 *
 *   1. LakeShimmerBands — thin, evenly-spaced semi-transparent cream
 *      horizontal bars drawn over the lake plane. Static geometry (Rule 8:
 *      no wave simulation), but visually communicates "water surface with
 *      subtle ripples" rather than "opaque paint slab."
 *
 *   2. NearGroundMist — a small alpha-ramped plane near the camera at
 *      ground level that gives the foreground terrain a subtle warm tint,
 *      reinforcing the warm-ochre fog's horizon read. Rule 5 says fog
 *      ends at ~200m; this is the near-band complement.
 *
 *   3. FarGroundHaze — a large low-opacity plane at the fog-far distance
 *      tinted with the FOG_COLOR, so the terrain-to-sky transition reads
 *      as atmospheric depth rather than a hard horizon line.
 *
 * All three use meshBasicMaterial + fog=false + transparent + depthWrite=
 * false + renderOrder carefully set so they composite correctly over the
 * existing terrain/water without z-fighting and without interacting with
 * fog. Not shader-authored.
 *
 * AESTHETIC-BRIEF compliance:
 *   - Rule 1 (no PBR): meshBasicMaterial only.
 *   - Rule 2 (no real-time shadows): no lights referenced.
 *   - Rule 5 (fog color parity): warm-ochre FOG_COLOR used for the haze.
 *   - Rule 8 (no wave sim): shimmer is static band geometry, not a shader.
 *   - Rule 9 (tier saturation): cream / warm-ochre / muted-blue palette-
 *     aligned colors only.
 *
 * Mounted as a sibling of SceneGraph's era groups. No manifest entry
 * required — these are pure visual enhancements, not authored content.
 */
import { useMemo } from 'react';
import * as THREE from 'three';
import { FOG_COLOR } from '../lib/const';

// Mirror of LAYOUT.lake from tools/generators/scene-layout.mjs. Copied
// (not imported) because the generator module lives outside the app's
// TypeScript include scope (it's a Node-only build-time module). These
// three numbers are stable — any change must land here AND in the
// generator file in the same commit.
const LAKE_CENTER: [number, number, number] = [-20, 0, -60];
const LAKE_WIDTH = 200;
const LAKE_DEPTH = 48;

// Lake ripple bar geometry cached at mount — one horizontal bar repeated
// via instancing would be cheaper but for v1 we author a small number of
// individual meshes for readability. ~12 bands at 0.06m thickness spread
// evenly across the 48m lake depth read as subtle horizontal ripples from
// any streetcar / walk POV looking at the lake.
const LAKE_BAND_COUNT = 14;
const LAKE_BAND_THICKNESS_Z = 0.35; // meters
const LAKE_BAND_Y = -0.74; // slightly above the water plane (water at y=-0.8)

// Near-ground warm haze: 1.5m-tall thin horizontal band at y ~ 0.2m, 250m
// square, with a warm-ochre tint at very low opacity. Blends into the
// ground cleanly without occluding silhouettes behind it.
const HAZE_Y = 0.25;
const HAZE_SIZE = 250;

// Far-distance sky haze: a large-radius cylindrical ring at the fog-far
// distance so the horizon reads as atmospheric depth. This is NOT a skybox
// — SkyDome owns the sky. This is a thin vertical gradient slab between
// the terrain horizon and the sky dome, matted in the warm-ochre fog color.
const FAR_RADIUS = 180; // just inside fog FOG_FAR=200
const FAR_HEIGHT = 40;

/**
 * LakeShimmerBands — LAKE_BAND_COUNT horizontal cream-tinted slivers
 * spanning the full lake width at evenly-spaced Z positions. Each band
 * has slight y-offset variation so the shimmer doesn't read as a grid.
 */
function LakeShimmerBands() {
  const bands = useMemo(() => {
    const items: Array<{
      position: [number, number, number];
      size: [number, number];
      opacity: number;
    }> = [];
    const width = LAKE_WIDTH;
    const depth = LAKE_DEPTH;
    const [cx, , cz] = LAKE_CENTER;
    // Distribute bands across the lake depth with slight variation
    for (let i = 0; i < LAKE_BAND_COUNT; i++) {
      const tz = (i + 0.5) / LAKE_BAND_COUNT; // 0..1
      const z = cz + (tz - 0.5) * (depth - 2.5);
      // Alternate the band lengths so the pattern doesn't read as uniform stripes
      const bandWidth = width * (0.55 + 0.4 * ((i * 13) % 7) / 7);
      const xOff = ((i * 7) % 5) - 2;
      items.push({
        position: [cx + xOff, LAKE_BAND_Y + (i % 2 === 0 ? 0.003 : 0), z],
        size: [bandWidth, LAKE_BAND_THICKNESS_Z],
        opacity: 0.17 + 0.08 * (((i * 3) % 5) / 5),
      });
    }
    return items;
  }, []);

  return (
    <group data-scene-enhance="lake-shimmer">
      {bands.map((b, i) => (
        <mesh
          key={`shimmer-${i}`}
          position={b.position}
          rotation={[-Math.PI / 2, 0, 0]}
          renderOrder={2}
        >
          <planeGeometry args={[b.size[0], b.size[1]]} />
          <meshBasicMaterial
            color="#EFE4D1"
            transparent
            opacity={b.opacity}
            depthWrite={false}
            fog={false}
            toneMapped={false}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
    </group>
  );
}

/**
 * NearGroundMist — single ground-hugging horizontal plane tinted warm-
 * ochre at low opacity. Covers the full walkable site so the terrain
 * reads as "bathed in afternoon light" rather than "flat color ground."
 */
function NearGroundMist() {
  return (
    <mesh
      position={[0, HAZE_Y, -80]}
      rotation={[-Math.PI / 2, 0, 0]}
      renderOrder={1}
      data-scene-enhance="near-ground-mist"
    >
      <planeGeometry args={[HAZE_SIZE, HAZE_SIZE]} />
      <meshBasicMaterial
        color={FOG_COLOR}
        transparent
        opacity={0.08}
        depthWrite={false}
        fog={false}
        toneMapped={false}
      />
    </mesh>
  );
}

/**
 * FarHorizonHaze — cylindrical shell near the fog-far distance tinted
 * with FOG_COLOR. Soft-blends the terrain-to-sky boundary so the horizon
 * reads as atmospheric depth instead of a hard band. Rendered with
 * BackSide so the camera sees the inside of the ring.
 */
function FarHorizonHaze() {
  return (
    <mesh
      position={[0, FAR_HEIGHT / 2, 0]}
      renderOrder={0}
      data-scene-enhance="far-horizon-haze"
    >
      <cylinderGeometry args={[FAR_RADIUS, FAR_RADIUS, FAR_HEIGHT, 32, 1, true]} />
      <meshBasicMaterial
        color={FOG_COLOR}
        transparent
        opacity={0.42}
        depthWrite={false}
        fog={false}
        toneMapped={false}
        side={THREE.BackSide}
      />
    </mesh>
  );
}

/**
 * SceneEnhancements — composite the three layers. Mounted in SceneGraph.
 */
export function SceneEnhancements() {
  return (
    <group data-scene-enhance="root">
      <FarHorizonHaze />
      <NearGroundMist />
      <LakeShimmerBands />
    </group>
  );
}
